/**
 * The sandbox's game as a playground definition — the ONE description both `defineSandboxTest` runners boot through
 * `mountPlayground`: the tab's (`bootRealRuntime`) and node's (`bootGeneratedGameNode`). The live preview boots the
 * project's own definition instead (its `sandbox.playground.ts`, which the scaffold writes as this same topology).
 *
 * A sandbox project has no Game or Area files: every client system runs in one client area and every server system
 * in one server area, both games tick at a fixed 60 Hz, and client N joins as `player-N`. These objects are that
 * topology exactly as `SandboxRuntime.ts` and `nodeGameBoot.ts` each built it before they shared this module.
 *
 * Structural types only: a static import of another @gotti package trips this package's `rootDir` (TS6059, see
 * nodeGameBoot.ts), so the definition is written out as the playground's `PlaygroundDefinition` shape and the
 * callers that hand it to `mountPlayground` own the cast.
 */

export const SANDBOX_GAME_ID = 'sandbox-game';
export const SANDBOX_AREA_ID = 'sandbox-area-1';

/** The id client `index` (0-based) joins as: `player-1`, `player-2`, … */
export const sandboxClientId = (index: number): string => `player-${index + 1}`;

/** Where one client draws: its canvas and the element around it. */
export interface SandboxSurface {
  canvas: HTMLCanvasElement;
  container: HTMLElement;
}

/** The asset seam test-utils' `initializer` takes: a byte source and already-namespaced paths to preload. */
export interface SandboxAssetSeam {
  source?: unknown;
  preload?: string[];
}

export interface SandboxPlaygroundOptions {
  clientSystems: unknown[];
  serverSystems: unknown[];
  /** One surface per client to boot, in join order. */
  surfaces: SandboxSurface[];
  /** One-way client↔server latency (ms): the transport's, and every client's `simulatedLatency`. */
  latencyMs: number;
  assets?: SandboxAssetSeam;
  /** Preloads declared on the server area file (`ServerAreaFile.preloads`). */
  serverPreloads?: { client?: string[]; server?: string[] };
  /** The definition's tests: a `defineSandboxTest` test its booter runs through the mount (`sandboxTestToPlayground`). */
  tests?: unknown[];
}

/** The playground definition `sandboxPlaygroundDefinition` returns (the subset of `PlaygroundDefinition` it fills). */
export interface SandboxPlaygroundDefinition {
  title: string;
  environment: Record<string, unknown>;
  clientConfig: Record<string, unknown>;
  mockConfig: { clientServerLatency: number; serverServerLatency: number };
  tests?: unknown[];
}

/** A booted client, as much of test-utils' `MockClient` as the sandbox reads. */
export interface SandboxClient {
  id: string;
  simulatedLatency?: number;
  simulatedPeerLatency?: number;
  process?: { $api?: Record<string, unknown> };
  $api?: Record<string, unknown>;
}

/** A booted environment, as much of test-utils' `MockEnvironment` as the sandbox reads. */
export interface SandboxEnvironment {
  clients?: Record<string, SandboxClient>;
  areas?: Record<string, { gameProcess?: { $api?: Record<string, unknown> }; process?: { $api?: Record<string, unknown> }; $api?: Record<string, unknown> }>;
  createClients: (count: number, options: Record<string, unknown>) => Promise<SandboxClient[]>;
}

/**
 * The client game: no systems of its own (they live on the area, so they start when the client joins it), fixed
 * 60 Hz, and an `api` that hands every client system the canvas the client draws on, its 2D context and the
 * element around it. Input focus is routed by the framework, so there is no focus flag on the api.
 */
export function createSandboxClientGame(): unknown {
  return {
    type: 'sandbox',
    isNetworked: true,
    fixedTimeStep: true,
    timeStep: 1 / 60,
    systems: [],
    async api(_gameData?: unknown, context?: { container?: HTMLElement; canvas?: HTMLCanvasElement }) {
      const canvas = context?.canvas;
      const container = context?.container;
      if (!canvas) return {};
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('sandbox: failed to get a 2D canvas context');
      return { canvas, ctx, container: container ?? document.body };
    },
  };
}

/** The client area: every client system. */
export const createSandboxClientArea = (systems: unknown[]): unknown => ({ type: 'sandbox-area', systems });

/** The server game: no systems of its own, fixed 60 Hz, every client starts in the one area, and no game-level api. */
export function createSandboxServerGame(): unknown {
  return {
    type: 'sandbox',
    fixedTimeStep: true,
    timeStep: 1 / 60,
    systems: [],
    async getInitialAreas() {
      return [SANDBOX_AREA_ID];
    },
    async api() {
      return {};
    },
  };
}

/** The server area: every server system, plus any preloads declared for it. */
export const createSandboxServerArea = (systems: unknown[], preloads?: { client?: string[]; server?: string[] }): unknown => ({
  type: 'sandbox-area',
  systems,
  preloads,
});

const joinGame = { gameId: SANDBOX_GAME_ID, initialAreas: [SANDBOX_AREA_ID] };

/** The sandbox topology for `surfaces.length` clients, as a playground definition. */
export function sandboxPlaygroundDefinition(options: SandboxPlaygroundOptions): SandboxPlaygroundDefinition {
  const { surfaces } = options;
  return {
    title: 'Sandbox',
    environment: {
      clientGame: createSandboxClientGame(),
      serverGame: { gameFile: createSandboxServerGame(), id: SANDBOX_GAME_ID },
      clientAreas: [createSandboxClientArea(options.clientSystems)],
      serverAreas: [{ areaFile: createSandboxServerArea(options.serverSystems, options.serverPreloads), id: SANDBOX_AREA_ID }],
      ...(options.assets ? { assets: options.assets } : {}),
    },
    clientConfig: {
      numberOfClients: surfaces.length,
      domSetup: (index: number) => surfaces[index] ?? surfaces[0],
      clientAuth: ({ index }: { index: number }) => ({ id: sandboxClientId(index), username: `Player ${index + 1}` }),
      joinGame,
    },
    mockConfig: { clientServerLatency: options.latencyMs, serverServerLatency: 0 },
    ...(options.tests ? { tests: options.tests } : {}),
  };
}

/** Every booted client's `simulatedLatency`, as the sandbox set it after each join before this module existed. */
export function applySandboxLatency(environment: SandboxEnvironment, latencyMs: number): void {
  for (const client of Object.values(environment.clients ?? {})) client.simulatedLatency = latencyMs;
}

/** Creates, authenticates and joins one more client as `player-(index+1)`, drawing on `surface`. */
export async function addSandboxClient(
  environment: SandboxEnvironment,
  options: { index: number; surface: SandboxSurface; latencyMs: number },
): Promise<SandboxClient> {
  const [client] = await environment.createClients(1, {
    numberOfClients: 1,
    domSetup: () => options.surface,
    clientAuth: { id: sandboxClientId(options.index), username: `Player ${options.index + 1}` },
    joinGame,
  });
  client.simulatedLatency = options.latencyMs;
  return client;
}

/** Client `index`'s merged `$api` (what its systems registered, plus the canvas api). */
export function sandboxClientApi(environment: SandboxEnvironment, index: number): Record<string, unknown> {
  const client = environment.clients?.[sandboxClientId(index)];
  return client?.process?.$api ?? client?.$api ?? {};
}

/** The server area's merged `$api`. */
export function sandboxServerApi(environment: SandboxEnvironment): Record<string, unknown> {
  const area = environment.areas?.[SANDBOX_AREA_ID];
  return area?.gameProcess?.$api ?? area?.process?.$api ?? area?.$api ?? {};
}

/**
 * A token no client's key listeners are scoped under: focusing it routes a key to no client. It is what a press for
 * a client the test never booted, or for one a step disconnected, focuses — in both runners — so that press is a
 * no-op, as it was before the runners booted through the playground.
 */
export const NO_SANDBOX_CLIENT = '__sandbox-no-client__';

/** What focusing test client `index` routes keys to: its input token while it is booted and connected, else no client. */
export const sandboxFocusTarget = (tokens: ReadonlyArray<number | undefined>, index: number): number | string =>
  tokens[index] ?? NO_SANDBOX_CLIENT;

/**
 * Ends a booted sandbox world. A system's `onStop` is game code: what it throws while the world stops is the game's
 * bug, not the test's verdict, so it is reported on the console and never raised — a test's outcome stays its own.
 */
export async function disposeSandboxMount(mount: { dispose(): Promise<void> }): Promise<void> {
  try {
    await mount.dispose();
  } catch (err) {
    console.warn('[sandbox] the game threw while it stopped:', err);
  }
}

/**
 * Runs the mount's one test — a `defineSandboxTest` test its definition carries — and then tears the boot down, however
 * the run ends: a boot lasts as long as its test, as the executor's own teardown made it before the test ran here.
 */
export async function runSandboxMountTest<Result, Progress>(
  mount: { runTests(options?: { onProgress?: (progress: Progress) => void }): Promise<{ tests: Result[]; error?: string }> },
  tearDown: () => Promise<void>,
  onProgress?: (progress: Progress) => void,
): Promise<Result> {
  try {
    const suite = await mount.runTests(onProgress ? { onProgress } : {});
    const [result] = suite.tests;
    if (!result) throw new Error(`[sandbox] the playground ran no test${suite.error ? `: ${suite.error}` : ''}.`);
    return result;
  } finally {
    await tearDown();
  }
}
